import * as LJS from '../../lib/littlejs.esm.js';
import { vec2 } from '../../lib/littlejs.esm.js';
import Globals from "../globals.ts";
import { tilesize, tilesheets } from '../const/tiles.ts';
import Camera from '../camera.ts';
import HUD from '../hud.ts';
import Player from '../entities/player.ts';
import Platform from '../entities/platform.ts';
import FallingPlatform from '../entities/fallingPlatform.ts';
import Spike from '../entities/spike.ts';
import Enemy from '../entities/enemy.ts';
import Lava from '../entities/lava.ts';
import RightWall from '../entities/rightWall.ts';
import LeftWall from '../entities/leftWall.ts';
import Smoke from '../entities/smoke.ts';
import Heart from '../entities/heart.ts';
let camera = new Camera();
let hud = new HUD();
let tileLayer;
let font = new LJS.FontImage;
// Game init
function init(level) {
    // Clear all existing objects to prevent accumulation between levels
    for (let i = LJS.engineObjects.length - 1; i >= 0; i--) {
        LJS.engineObjects[i].destroy();
    }
    // Recreate camera and hud after clearing
    camera = new Camera();
    hud = new HUD();
    // Set gravity for platformer
    LJS.setGravity(vec2(0, -.02));
    let mapWidth = Globals.WELL_WIDTH + 2;
    // Create background tile layer (no collisions, renders behind)
    tileLayer = new LJS.TileLayer(vec2(0, 0), vec2(mapWidth, 20), new LJS.TileInfo(vec2(0), vec2(tilesize), tilesheets.DARK));
    tileLayer.renderOrder = 0;
    // Spawn player at center of map
    let player = new Player(vec2(mapWidth / 2, 0));
    Globals.player = player;
    // Spawn the walls
    for (let y = 1; y < 60; y++) {
        new LeftWall(y);
        new RightWall(y);
    }
    // Spawn the starting platforms just under the player
    for (let x = Globals.WELL_WIDTH / 2 - 5; x < Globals.WELL_WIDTH / 2 + 5; x++) {
        new Platform(vec2(x, -1), null);
    }
    // Instanciate the lava and the smoke
    let lava = new Lava(vec2(0, -15));
    let smoke = new Smoke(vec2(0, 15));
    // Generate the next chunk
    generateLevel();
    // Setup camera
    camera.init(Globals.player.pos.copy(), Globals.player);
}
function update() {
    // Check if game is in pause state - if so, don't update game logic
    if (Globals.gameState === 'pause' || Globals.gameState === 'gameOver') {
        return;
    }
    generateLevel();
    // Depending on which world we are in, we set the screen to black or white
    document.body.style.backgroundColor = Globals.world == Globals.LIGHT ? "white" : "black";
    tileLayer.tileInfo.textureIndex = Globals.world == Globals.LIGHT ? tilesheets.LIGHT : tilesheets.DARK;
    tileLayer.redraw();
}
function updatePost() {
    // Post update (camera, etc.)
}
function render() {
    // Custom render
}
function renderPost() {
    camera.update();
    hud.render();
}
/**
 * World generation
 * Basic for now : We generate a chunk with a height of 60
 * containing 5 levels separated by a height of 3 (enough to
 * jump from one level to the next higher one)
 * There is a 60 levels buffer between the current level of
 * the player and the generation of new levels (so we are sure
 * that they are out of screen)
 */
function generateLevel() {
    if (Globals.depth - Globals.chunkSize * 2 < Globals.lastGenerated) {
        // We start from up and go down
        // We first spawn the walls
        for (let y = 0; y > -Globals.chunkSize; y -= 1) {
            new LeftWall(Globals.generationDepth + y);
            new RightWall(Globals.generationDepth + y);
        }
        /**
         * We generat the platforms by sub-chunks of 15 blocks (genSize)
         * We generate platforms by group, with a minimum of platforms per group (the minimum number
         * reduces with the depth), a chance of a bonus platform on the group (probability also goes down),
         * and once the group is finished, we have a chance of generating a new one on the next blocks (you
         * guessed it, probability also goes down for this)
         */
        let genSize = 15;
        let groupSize = 5; // Minimum number of platforms per group
        let suppProb = 0.1; // Probability of having one more platform on the group
        let groupProb = 0.3; // Probability of creating a new group
        let fallProb = 0.1; // Probability that we are going to use falling platforms
        let worldProb = 0.9; // Probability that the platform is a normal one (existing in both worlds)
        let spikeProb = 0.1; // Probability to have one spike in the group
        let enemyProb = 0; // Probability to have one enemy on the group
        let genHeart = (Globals.lastHeartDepth - Globals.generationDepth) > 100;
        // Difficulty increase
        if (Globals.generationDepth <= -100) {
            groupSize = 3;
            suppProb = 0.05;
            worldProb = 0.75;
            enemyProb = 0.1;
        }
        if (Globals.generationDepth <= -200) {
            worldProb = 0.6;
            spikeProb = 0.2;
        }
        if (Globals.generationDepth <= -300) {
            worldProb = 0.4;
            spikeProb = 0.3;
            enemyProb = 0.2;
        }
        if (Globals.generationDepth <= -400) {
            suppProb = 0.01;
            worldProb = 0.2;
            spikeProb = 0.4;
        }
        if (Globals.generationDepth <= -500) {
            worldProb = 0;
            spikeProb = 0.5;
            enemyProb = 0.3;
            fallProb = 0.25;
        }
        if (Globals.generationDepth <= -1000) {
            suppProb = 0.1;
            spikeProb = 0.75;
            enemyProb = 0.5;
            fallProb = 0.5;
        }
        for (let i = 0; i >= -Globals.chunkSize / genSize; i--) {
            let y = Globals.generationDepth - genSize * i;
            // First we generate groups of solid platforms at random heights
            let randY = Globals.rand.randInt(0, genSize);
            let groupCount = 0;
            let hasSpike = false;
            let hasEnemy = false;
            let isFalling = Globals.rand.randFloat() < fallProb;
            let groupWorld = getRandomWorld(worldProb);
            for (let x = 1; x < Globals.WELL_WIDTH - 1; x++) {
                // We do not generate anything above the spawn
                if (y + randY <= -1) {
                    if (groupCount >= groupSize) {
                        // If we finished the group, we have some chance of getting a bonus platform (always solid)
                        if (Globals.rand.randFloat() < suppProb && groupCount == groupSize) {
                            new Platform(vec2(x, y + randY), groupWorld);
                            groupCount++;
                        }
                        else if (!hasSpike && Globals.rand.randFloat() < enemyProb && groupCount == groupSize && !hasEnemy) {
                            // We also have a chance of generating a monster
                            new Enemy(vec2(x - 1, y + randY + 1), groupWorld);
                            hasEnemy = true;
                        }
                        else {
                            // We look if we can create a new group
                            if (Globals.rand.randFloat() < groupProb) {
                                randY = Globals.rand.randInt(0, genSize);
                                groupCount = 0;
                                isFalling = Globals.rand.randFloat() < fallProb;
                                groupWorld = getRandomWorld(worldProb);
                                hasEnemy = false;
                                hasSpike = false;
                            }
                            else {
                                groupCount++;
                            }
                        }
                    }
                    else {
                        // When generating a platform, we have a chance to generate a heart
                        // or a spike. In order for the spike to not be in the middle of a
                        // group we make sure that they spawn on the left or the right
                        // We look if we can generate a heart
                        if (genHeart && groupCount == 0 && Globals.rand.randFloat() < 0.1) {
                            new Heart(vec2(x, y + randY + 1), groupWorld);
                            genHeart = false;
                            Globals.lastHeartDepth = y;
                        }
                        else if (!hasSpike && (groupCount == 0 || groupCount == groupSize - 1)) {
                            if (Globals.rand.randFloat() < spikeProb) {
                                new Spike(vec2(x, y + randY + 1), groupWorld);
                                hasSpike = true;
                            }
                        }
                        if (isFalling) {
                            new FallingPlatform(vec2(x, y + randY), groupWorld);
                        }
                        else {
                            new Platform(vec2(x, y + randY), groupWorld);
                        }
                        groupCount++;
                    }
                }
            }
        }
        Globals.lastGenerated -= Globals.chunkSize;
        Globals.generationDepth -= Globals.chunkSize;
    }
}
/**
 * Returns the world for the next group of platforms / objects
 * @param worldProb - The probabiliy that the group is in both world
 * @returns - The world of the group (null for both, or 'dark' | 'light')
 */
function getRandomWorld(worldProb) {
    // If the epilepsy-safe mode is activated, we only return null
    if (Globals.epilepsySafe) {
        return null;
    }
    let groupWorld = Globals.rand.randFloat() < worldProb ? null : 'dark';
    if (groupWorld == 'dark' && Globals.rand.randFloat() < 0.5) {
        groupWorld = 'light';
    }
    return groupWorld;
}
export { init, update, updatePost, render, renderPost };
